import React, { useEffect, useState } from "react";
import { adminFetchBtwMailContext } from "../core/axios/endpoints";

const fmtEUR = (n, digits = 2) =>
  n === null || n === undefined
    ? "-"
    : `€ ${Number(n).toLocaleString("nl-NL", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })}`;

const Row = ({ label, value, strong = false }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-2.5 text-sm last:border-b-0">
    <dt className="col-span-1 text-gray-500">{label}</dt>
    <dd
      className={`col-span-2 tabular-nums ${
        strong ? "font-semibold text-gray-900" : "text-gray-900"
      }`}
    >
      {value ?? "-"}
    </dd>
  </div>
);

// Berekening van de verwachte teruggave, zoals de server die maakt voor de
// klantmails ({{btwFactuur}}, {{afdrachtEersteJaar}}, {{nettoTeruggave}}).
// Dit blok laat zien welke formule we hanteren, zodat een admin weet wat er
// in een mail komt te staan. De berekening zelf staat op de server in
// utils/btwRefundCalc.ts; hier wordt niets herberekend.
const BtwBerekeningSection = ({ applicationId, reloadKey }) => {
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!applicationId) return undefined;
    let cancelled = false;
    adminFetchBtwMailContext(applicationId)
      .then((res) => {
        if (!cancelled) setCtx(res?.data ?? res);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.error ?? "Berekening laden mislukt.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId, reloadKey]);

  const calc = ctx?.calc;

  return (
    <section className="mb-5 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">
          Berekening teruggave
        </h3>
        {calc?.compleet ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-emerald-200">
            Verwacht {fmtEUR(calc.nettoTeruggave, 0)}
          </span>
        ) : null}
      </div>

      {error ? (
        <p className="text-sm text-rose-700">{error}</p>
      ) : !calc ? (
        <p className="text-sm text-gray-400">Laden…</p>
      ) : (
        <>
          <dl>
            <Row label="Aankoopbedrag (incl. btw)" value={fmtEUR(calc.aankoopbedragIncl)} />
            <Row label="BTW op de factuur" value={fmtEUR(calc.btwFactuur)} />
            <Row
              label="Capaciteit batterij"
              value={
                calc.capaciteitKwh !== null
                  ? `${Number(calc.capaciteitKwh).toLocaleString("nl-NL")} kWh`
                  : "-"
              }
            />
            <Row
              label="Afdracht eerste jaar"
              value={
                calc.afdrachtEersteJaar !== null
                  ? `${fmtEUR(calc.afdrachtEersteJaar, 0)} (${Number(
                      calc.capaciteitKwh,
                    ).toLocaleString("nl-NL")} kWh x ${fmtEUR(calc.afdrachtPerKwh, 0)})`
                  : "-"
              }
            />
            <Row
              label="Netto teruggave"
              value={fmtEUR(calc.nettoTeruggave, 0)}
              strong
            />
          </dl>

          {calc.warnings?.length > 0 ? (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold">Berekening niet compleet</p>
              <ul className="mt-1 list-disc pl-4">
                {calc.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
              <p className="mt-1">
                Mails met bedragen kunnen pas verstuurd worden als dit is
                aangevuld bij &quot;Gegevens voor BTW-teruggave&quot;.
              </p>
            </div>
          ) : null}

          <details className="mt-3 text-xs text-gray-500">
            <summary className="cursor-pointer select-none hover:text-gray-700">
              Welke formule hanteren we?
            </summary>
            <ul className="mt-2 list-disc space-y-0.5 pl-4">
              {(calc.formule ?? []).map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <p className="mt-2">
              De afdracht is de eenmalige BTW over de opbrengst van de
              verhandelde energie in het eerste jaar, vooraf voorzichtig
              geschat. Deze bedragen komen als variabelen in de klantmails.
            </p>
          </details>
        </>
      )}
    </section>
  );
};

export default BtwBerekeningSection;
